import {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {skipToken} from '@reduxjs/toolkit/query/react';
import {useGetSettingsQuery, useSaveSettingsMutation} from '../../redux/rtk/settingsApi';
import {RootState} from '../../redux/store';

export interface Template<T> {
    id: string;
    name: string;
    data: T;
}

export function useTemplates<T>(category: string) {
    const projectId = useSelector((state: RootState) => state.project.selectedProject?.project_id);
    const {data: remoteSettings, isLoading} = useGetSettingsQuery(
        projectId ? {category, projectId} : skipToken
    );
    const [saveSettings] = useSaveSettingsMutation();

    const templates = useMemo(() => {
        if (!remoteSettings || !remoteSettings.value) return [];
        try {
            const parsed = typeof remoteSettings.value === 'string'
                ? JSON.parse(remoteSettings.value)
                : remoteSettings.value;
            return Array.isArray(parsed) ? (parsed as Template<T>[]) : [];
        } catch (e) {
            console.error('Failed to parse templates from API', e);
            return [];
        }
    }, [remoteSettings]);

    const saveTemplates = useCallback(async (newTemplates: Template<T>[]) => {
        if (!projectId) return;
        await saveSettings({
            category,
            projectId,
            data: {value: newTemplates}
        });
    }, [category, projectId, saveSettings]);

    const addTemplate = useCallback(async (name: string, data: T) => {
        const newTemplate: Template<T> = {
            id: crypto.randomUUID(),
            name,
            data
        };
        const nextTemplates = [...templates, newTemplate];
        await saveTemplates(nextTemplates);
        return newTemplate;
    }, [templates, saveTemplates]);

    const updateTemplate = useCallback(async (id: string, data: T) => {
        const nextTemplates = templates.map(t => t.id === id ? {...t, data} : t);
        await saveTemplates(nextTemplates);
    }, [templates, saveTemplates]);

    const deleteTemplate = useCallback(async (id: string) => {
        const nextTemplates = templates.filter(t => t.id !== id);
        await saveTemplates(nextTemplates);
    }, [templates, saveTemplates]);

    return {
        templates,
        isLoading,
        addTemplate,
        updateTemplate,
        deleteTemplate
    };
}
